import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
    getAcquisitionCalendarApi,
    getAcquisitionDictTreeApi,
    getAcquisitionByDateApi,
    getAcquisitionByIdApi,
    getAcquisitionActivitiesDataApi,
    postAcquisitionCommentApi,
    putAcquisitionCommentApi,
    postAcquisitionActivitiesDataApi,
    getPreplotSListApi,
    getPreplotLinesApi,
    getGeometryOBNApi
} from 'api'
import { BoundarySlice } from 'redux/reducers'

const initialState = {
    calendarInfo: null, // 被点击的日历信息
    calendarData: null, // 日历上当前范围内显示的总结填写信息
    dictData: null, // 数据字典
    listData: null, // 回显数据
    locationName: '', // location 名称
    locationId: '', // location id
    save: false,
    id: '',
    lineListData: null, // 线列表
}

/* 获取日历上的数据 */
export const getAcquisitionCalendarAction = createAsyncThunk(
    'AcquisitionSlice/getAcquisitionCalendarAction',
    async (params, thunkAPI) => {
        try {
            const { data } = await getAcquisitionCalendarApi(params)
            return data.data
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

// 数据字典
export const getAcquisitionDictDataAction = createAsyncThunk(
    'AcquisitionSlice/getAcquisitionDictDataAction',
    async (dictType, thunkAPI) => {
        const acquisitionTypeDataDict = getAcquisitionDictTreeApi(dictType[0])
        const acquisitionStatusDataDict = getAcquisitionDictTreeApi(dictType[1])
        const acquisitionAreaTypeDataDict = getAcquisitionDictTreeApi(dictType[3])
        const preplotList = getPreplotSListApi(dictType[2])
        const geometryOBN = getGeometryOBNApi(dictType[2])
        return Promise.all([acquisitionTypeDataDict, acquisitionStatusDataDict, preplotList, geometryOBN, acquisitionAreaTypeDataDict])
            .then(dictData => dictData)
            .catch(error => thunkAPI.dispatch(BoundarySlice.actions.addError(error)))
    }
)

// 激发文件线
export const getAcquisitionLinesDataAction = createAsyncThunk(
    'AcquisitionSlice/getAcquisitionLinesDataAction',
    async (id, thunkAPI) => {
        try {
            const lineListData = await getPreplotLinesApi(id) // 通过文件 id 查询
            return lineListData?.data?.data
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 通过日期获取 id */
export const getAcquisitionByDateAction = createAsyncThunk(
    'AcquisitionSlice/getAcquisitionByDateAction',
    async (listDataParams, thunkAPI) => {
        try {
            const commentData = await getAcquisitionByDateApi(listDataParams) // 通过日期查询 id
            return commentData?.data?.data?.id
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 获取数据 */
export const getAcquisitionDataAction = createAsyncThunk(
    'AcquisitionSlice/getAcquisitionDataAction',
    async (id, thunkAPI) => {
        const commentData = getAcquisitionByIdApi(id) // 通过 id 查询
        const activitiesData = getAcquisitionActivitiesDataApi(id)
        return Promise.all([commentData, activitiesData])
            .then(listData => listData)
            .catch(error => thunkAPI.dispatch(BoundarySlice.actions.addError(error)))
    }
)

/* 保存数据 */
export const postAcquisitionDataAction = createAsyncThunk(
    'AcquisitionSlice/postAcquisitionDataAction',
    async ({ id, commentParams, activitiesParams }, { dispatch }) => {
        let vid
        try {
            if (!id) {
                const commentData = await postAcquisitionCommentApi(commentParams) // 新增
                vid = commentData.data.data.id
                await postAcquisitionActivitiesDataApi(activitiesParams, vid)
                dispatch(AcquisitionSlice.actions.addId(vid))
                return { isTrue: true, id: vid } // 已保存状态
            } else {
                vid = id
                await putAcquisitionCommentApi(commentParams, id) // 修改
                await postAcquisitionActivitiesDataApi(activitiesParams, id)
                return { isTrue: true, id } // 已保存状态
            }
        } catch (error) {
            dispatch(AcquisitionSlice.actions.addId(vid)) // 失败时，保存 id
            dispatch(BoundarySlice.actions.addError(error)) // 边界错误状态
        }
    }
)

export const AcquisitionSlice = createSlice({
    name: 'AcquisitionSlice',
    initialState,
    reducers: {
        /* 日历点击信息 */
        addCalendarInfo: (state, action) => {
            state.calendarInfo = action.payload
        },
        /* 清空日历与详情页信息 */
        clearListData: state => {
            state.listData = null
            state.calendarData = null
            state.save = false
            state.id = ''
        },
        /* 当前日历主 id */
        addId: (state, action) => {
            state.id = action.payload
        },
        /* location 名称 */
        addLocationName: (state, action) => {
            state.locationName = action.payload
        },
        /* location id */
        addLocationId: (state, action) => {
            state.locationId = action.payload
        },
        /* 清除激发文件数据 */
        clearFielData: (state, action) => {
            state.lineListData = null
        },
    },
    extraReducers: {
        /* 获取日历上的数据 */
        [getAcquisitionCalendarAction.fulfilled.type]: (state, action) => {
            state.calendarData = action.payload
        },

        /* 处理数据字典 */
        [getAcquisitionDictDataAction.fulfilled.type]: (state, action) => {
            state.dictData = action.payload
        },

        /* 线 */
        [getAcquisitionLinesDataAction.fulfilled.type]: (state, action) => {
            state.lineListData = action.payload
        },

        /* 提交总结数据 */
        [postAcquisitionDataAction.fulfilled.type]: (state, action) => {
            state.save = action.payload
        },

        /* 获取回显数据 */
        [getAcquisitionDataAction.fulfilled.type]: (state, action) => {
            state.listData = action.payload
        },

        /* 通过日期，获取 id */
        [getAcquisitionByDateAction.fulfilled.type]: (state, action) => {
            state.id = action.payload
        },
    },
})
